/**
 * The dials of the Overview (DESIGN UD-36, ED-180 to ED-189): pure functions from the engine's calibrated index and the
 * model card's figures to what the dial draws and says. The authorship dial reads the calibrated value as "N in 100" at
 * the reference prior, always with its conditions; the translation dial is an uncalibrated 0 to 100 measure with no zones,
 * no percentage and no "in 100". Nothing is computed from the text here: the needle angle is a scale, every other figure is
 * copied from the engine's result or `figuresFor`. The web view and the exports (`worker/report.ts`, the CLI) share this file
 * so the sentence and its conditions are the same everywhere.
 */
import type { CalibratedIndex } from '../engine/ensemble/calibrated.ts';
import { DIAL } from '../web/strings-dial.ts';
import { NOTICE_EXPLAIN, ceilingBelowReference } from '../web/strings-explain.ts';
import { figuresFor } from './model-figures.ts';

/** The dial's sweep: the needle turns from the left end (0) to the right end (100) through this many degrees. */
export const DIAL_SWEEP_DEGREES = 180;

const clamp100 = (v: number): number => Math.min(100, Math.max(0, v));

/** The needle's rotation from the left end, in degrees, for a value on the 0 to 100 scale. */
export const needleDegrees = (value: number): number =>
	(clamp100(value) / 100) * DIAL_SWEEP_DEGREES;

/** A mark on the dial's scale: where, which kind, and the sentence that says what it is. */
export interface DialMark {
	id: 'reference' | 'ceiling';
	value: number;
	label: string;
}

export interface AuthorshipDial {
	state: 'reading' | 'greyed';
	/** The calibrated value on 0 to 100 (one decimal is the engine's), or null when greyed. */
	value: number | null;
	/** The needle's rotation from the left end, or null when greyed. */
	degrees: number | null;
	/** "N in 100": the large figure under the dial; null when greyed. */
	reading: string | null;
	/** The B sentence (UD-36); null when greyed. */
	sentence: string | null;
	/** The conditions, always present with a reading (UD-36 conditions); empty when greyed. */
	conditions: string[];
	/** The marks on the scale, in the order of the legend. */
	marks: DialMark[];
	/** The text-sampling interval as an arc on 0 to 100, or null. */
	interval: [number, number] | null;
	/** The sentence that explains the shaded arc, when there is one. */
	intervalNote: string | null;
	/** Why there is no needle, when there is none. */
	reason: string | null;
	/** The accessible name of the graphic: the reading and its conditions, or the reason. */
	aria: string;
}

export interface TranslationDial {
	state: 'reading' | 'greyed';
	value: number | null;
	degrees: number | null;
	aria: string;
}

const whole = (v: number): string => String(Math.round(clamp100(v)));
const one = (v: number): string => v.toFixed(1);
const group = (g: string): string => g.replace('-', '–');

/** The conditions that always stand beside the reading. `ci.group` is set whenever there is a value. */
export function dialConditions(ci: CalibratedIndex, modelId: string | undefined): string[] {
	const A = DIAL.authorship;
	const fig = figuresFor(modelId);
	const err = ci.group ? fig.calibrationError[ci.group] : undefined;
	return [
		A.notThisDocument,
		A.corpus,
		err && ci.group
			? A.calibrationError(group(ci.group), one(err.ece * 100), one(err.upper * 100))
			: A.noCalibrationError,
	];
}

/** The authorship dial of a result. `ci` is `ensemble.calibratedIndex` (undefined when no calibrated model applied). */
export function authorshipDial(
	ci: CalibratedIndex | undefined,
	modelId: string | undefined
): AuthorshipDial {
	const A = DIAL.authorship;
	if (!ci || ci.index === null) {
		const reason = ci ? (ci.reason ?? ci.statusPhrase) : NOTICE_EXPLAIN.uncalibrated;
		return {
			state: 'greyed',
			value: null,
			degrees: null,
			reading: null,
			sentence: null,
			conditions: [],
			marks: [],
			interval: null,
			intervalNote: null,
			reason,
			aria: `${A.ariaLead} ${A.ariaGreyed(reason)}`,
		};
	}
	const fig = figuresFor(modelId);
	const marks: DialMark[] = [
		{
			id: 'reference',
			value: ci.referenceIndex,
			label: A.referenceMark(one(ci.referenceIndex)),
		},
	];
	// a length group whose map stops below the reference point: mark where its index stops (the group's own point is on the
	// raw logit and has no place on this scale)
	// (a map that tops out at or above the reference point, as the REFIT-S short-text map does at the clip, needs no mark)
	const cap = ci.group ? (fig.shortPoint[ci.group] ?? fig.unreachable[ci.group]) : undefined;
	if (cap && ceilingBelowReference(cap.ceiling, ci.referenceIndex))
		marks.push({
			id: 'ceiling',
			value: cap.ceiling * 100,
			label: A.ceilingMark(one(cap.ceiling * 100)),
		});
	const iv = ci.interval
		? ([ci.interval.index[0], ci.interval.index[1]] as [number, number])
		: null;
	const sentence = A.sentence(whole(ci.index));
	const conditions = dialConditions(ci, modelId);
	const aria = [
		A.ariaLead,
		A.ariaNeedle(whole(ci.index), sentence),
		...conditions,
		...(iv ? [A.ariaInterval(one(iv[0]), one(iv[1])), A.intervalNote] : []),
	].join(' ');
	return {
		state: 'reading',
		value: ci.index,
		degrees: needleDegrees(ci.index),
		reading: A.reading(whole(ci.index)),
		sentence,
		conditions,
		marks,
		interval: iv,
		intervalNote: iv ? A.intervalNote : null,
		reason: null,
		aria,
	};
}

/** The translation dial: the uncalibrated translation index (or MT-01 score) on 0 to 100, with no zones and no marks. */
export function translationDial(value: number | null | undefined): TranslationDial {
	const T = DIAL.translation;
	if (value === null || value === undefined || Number.isNaN(value))
		return {
			state: 'greyed',
			value: null,
			degrees: null,
			aria: `${T.ariaLead} ${T.ariaGreyed}`,
		};
	return {
		state: 'reading',
		value,
		degrees: needleDegrees(value),
		aria: `${T.ariaLead} ${T.ariaNeedle(whole(value))}`,
	};
}

/**
 * The lines the exports add where they carry the index: the sentence, then its conditions. Empty when there is no needle
 * (the exports already say why there is no calibrated index).
 */
export function dialTextLines(
	ci: CalibratedIndex | undefined,
	modelId: string | undefined
): string[] {
	const d = authorshipDial(ci, modelId);
	return d.sentence ? [d.sentence, ...d.conditions] : [];
}
